// src/components/DynamicPageHeader.tsx
// Komponen header dinamis untuk halaman worksheet dan kontak

import React from 'react';
import BlurText from '../TextAnimations/BlurText/BlurText';
import AnimatedContent from '../Animations/AnimatedContent/AnimatedContent';
import FadeContent from '../Animations/FadeContent/FadeContent';

interface DynamicPageHeaderProps {
  title: string;
  subtitle?: string;
  description?: string;
  backgroundImageUrl?: string;
  className?: string;
  loading?: boolean;
  error?: string | null;
}

const DynamicPageHeader: React.FC<DynamicPageHeaderProps> = ({
  title,
  subtitle,
  description,
  backgroundImageUrl,
  className = '',
  loading = false,
  error = null
}) => {
  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-lg p-8 text-center ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-6 bg-gray-300 rounded w-48 mx-auto mb-2"></div>
          <div className="h-4 bg-gray-300 rounded w-96 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-2xl shadow-lg p-8 text-center ${className}`}>
        <div className="text-red-500">
          <p className="text-lg font-semibold mb-2">Error memuat header</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-200 ${className}`}>
      {/* Background Pattern */}
      <div 
        className="h-32 bg-gradient-to-r from-blue-600 to-purple-700 relative"
        style={{
          backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20"></div>
        {/* Decorative circles */}
        <div className="absolute top-4 right-4 w-16 h-16 bg-white/20 rounded-full animate-pulse"></div>
        <div className="absolute top-8 right-12 w-8 h-8 bg-white/30 rounded-full animate-pulse"></div>
        <div className="absolute bottom-4 left-4 w-12 h-12 bg-white/20 rounded-full animate-pulse"></div>
      </div>

      <div className="px-6 py-8">
        {/* Header Content */}
        <div className="text-center">
          <AnimatedContent
            {...({
              direction: "vertical",
              distance: 50,
              duration: 1,
            } as any)}
          >
            <BlurText 
              text={title}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              animateBy="words"
              direction="top"
              delay={10}
              stepDuration={0.6}
              onAnimationComplete={() => {}}
            />
          </AnimatedContent>

          {subtitle && (
            <FadeContent delay={0.5}>
              <BlurText 
                text={subtitle}
                className="text-xl text-blue-600 font-semibold mb-4"
                animateBy="words"
                direction="bottom"
                delay={10}
                stepDuration={0.4}
                onAnimationComplete={() => {}}
              />
            </FadeContent>
          )}

          {description && (
            <FadeContent delay={0.8}>
              <BlurText 
                text={description}
                className="text-gray-700 text-lg leading-relaxed max-w-3xl mx-auto"
                animateBy="words"
                direction="top"
                delay={10}
                stepDuration={0.3}
                onAnimationComplete={() => {}}
              />
            </FadeContent>
          )}
        </div>

        {/* Decorative bottom border */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center">
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicPageHeader;